import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { dlrPolicies, type DLRPolicy, type DLRPolicyInput } from "@/app/master";
import { loadingOf, useList } from "@/app/queries";
import { Alert, Button, EmptyState, Input, PageHeader, Table, TableWrap, Th, Toolbar, ToolbarCount } from "@/ui";
import { DLRPolicyForm } from "./DLRPolicyForm";
import { dlrPolicyDraft, dlrPolicyInput } from "./dlrPolicyDraft";

/** 回执策略主数据；账号绑定后只作用于新消息，旧消息保留自己的策略版本。 */
export function DLRPolicies() {
  const canWrite = useCanWrite();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<{ row: DLRPolicy | null } | null>(null);
  const list = useList("dlr-policies", dlrPolicies);
  const rows = (list.data ?? []).filter(p => p.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <>
      <PageHeader title="回执策略" description="在客户账号里绑定。保存后只影响新消息，真实投递、重试与计费口径不变。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing({ row: null })}>新建策略</Button>} />
      {list.error && <Alert tone="danger" title="加载失败">{list.error.message}</Alert>}
      <Toolbar right={<ToolbarCount>{rows.length} 条</ToolbarCount>}>
        <Input size="sm" className="ax-search" aria-label="搜索策略" placeholder="搜索策略名称" value={q} onChange={e => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(list)}>
        {rows.length ? (
          <Table>
            <thead><tr><Th w="name">名称</Th><Th numeric w="num">超时（秒）</Th><Th numeric w="num">失败转成功</Th><Th w={72}>版本</Th><Th w="status">状态</Th><Th /></tr></thead>
            <tbody>{rows.map(p => (
              <tr key={p.id}>
                <td>{p.name}</td><td className="ax-num">{p.timeout_sec}</td><td className="ax-num">{p.failure_success_bps / 100}%</td>
                <td className="ax-mono">v{p.version}</td><td><EntityStatusPill status={p.status} /></td>
                <td><Button variant="ghost" size="sm" onClick={() => setEditing({ row: p })}>{canWrite ? "编辑" : "查看"}</Button></td>
              </tr>
            ))}</tbody>
          </Table>
        ) : !list.isPending && <EmptyState title="没有回执策略">未绑定的账号保持真实回执。</EmptyState>}
      </TableWrap>
      {editing && (
        <EntityDrawer title={editing.row ? `编辑回执策略 ${editing.row.name}` : "新建回执策略"}
          initial={dlrPolicyDraft(editing.row ?? undefined)} convert={dlrPolicyInput}
          save={(body: DLRPolicyInput) => editing.row ? dlrPolicies.update(editing.row.id, body) : dlrPolicies.create(body)}
          invalidate={["dlr-policies"]} message="已保存回执策略" deletion={editing.row ? deleteTarget("回执策略", editing.row.name, dlrPolicies, editing.row.id, ["dlr-policies"]) : undefined} onClose={() => setEditing(null)}>
          {f => <DLRPolicyForm draft={f.draft} errors={f.errors} set={f.set} />}
        </EntityDrawer>
      )}
    </>
  );
}
